import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { IconComponent } from '../icon/icon.component';
import type { UiIconName } from '../icon/icon.component';

export type UiAlertType = 'success' | 'error' | 'warning' | 'info';

function defaultIcon(t: UiAlertType): UiIconName {
  switch (t) {
    case 'success':
      return 'check';
    case 'error':
      return 'alert-circle';
    case 'warning':
      return 'alert-triangle';
    case 'info':
    default:
      return 'info';
  }
}

@Component({
  selector: 'app-alert',
  standalone: true,
  imports: [IconComponent],
  template: `
    <div [attr.role]="'alert'" class="ui-alert" [class]="typeClass()">
      <span class="ui-alert__icon" aria-hidden="true">
        <app-icon [name]="resolvedIcon()" [size]="18" />
      </span>
      <div class="ui-alert__body">
        <ng-content />
      </div>
      @if (dismissible()) {
        <button type="button" class="ui-alert__close" (click)="dismiss.emit($event)" aria-label="Dismiss alert">
          <app-icon name="x" [size]="14" />
        </button>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
      font-family: var(--ui-font-sans);
    }
    .ui-alert {
      box-sizing: border-box;
      display: flex;
      align-items: center;
      gap: var(--ui-space-2);
      min-height: var(--ui-control-height);
      height: var(--ui-control-height);
      border-radius: var(--ui-radius-control);
      padding: 0 var(--ui-space-3);
      border: 1px solid transparent;
    }
    .ui-alert__icon {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
    }
    .ui-alert__body {
      flex: 1;
      min-width: 0;
      font-size: var(--ui-type-body-size);
      line-height: 1.25;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .ui-alert__close {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
      color: inherit;
      line-height: 1;
    }
    .ui-alert__close:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
      border-radius: var(--ui-radius-sm);
    }
    .ui-alert--success {
      background: color-mix(in srgb, var(--ui-color-success) 18%, var(--ui-color-surface));
      border-color: color-mix(in srgb, var(--ui-color-success) 35%, var(--ui-color-surface));
      color: var(--ui-color-text);
    }
    .ui-alert--error {
      background: color-mix(in srgb, var(--ui-color-danger) 15%, var(--ui-color-surface));
      border-color: color-mix(in srgb, var(--ui-color-danger) 40%, var(--ui-color-surface));
      color: var(--ui-color-text);
    }
    .ui-alert--warning {
      background: color-mix(in srgb, var(--ui-color-warning) 22%, var(--ui-color-surface));
      border-color: color-mix(in srgb, var(--ui-color-warning) 45%, var(--ui-color-surface));
      color: var(--ui-color-text);
    }
    .ui-alert--info {
      background: color-mix(in srgb, var(--ui-color-info) 18%, var(--ui-color-surface));
      border-color: color-mix(in srgb, var(--ui-color-info) 40%, var(--ui-color-surface));
      color: var(--ui-color-text);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertComponent {
  readonly type = input<UiAlertType>('info');
  readonly dismissible = input(false);
  /** Override the default icon for this alert type. */
  readonly icon = input<UiIconName | null>(null);

  readonly dismiss = output<MouseEvent>();

  readonly resolvedIcon = computed(() => this.icon() ?? defaultIcon(this.type()));

  protected typeClass(): string {
    return `ui-alert--${this.type()}`;
  }
}
